import { useMatch } from "react-router-dom-v5-compat"
import styled from "styled-components"

import {
  ButtonStyleReset,
  colors,
  colorScale,
  IconButton,
  spacing,
  Text,
} from "@minuthq/meatball-ui-react"

import { TConversationStatus } from "src/data/guests/types/guestTypes"
import { useTranslate } from "src/i18n/useTranslate"
import { useRouter } from "src/router/useRouter"
import OptionsHorizontal from "src/ui/icons/options-horizontal.svg"
import { formatTimeAgo } from "src/utils/l10n"

export function GuestConversationNavItem({
  title,
  summary,
  conversationStatus,
  lastSentMessageAt,
  onOptionsClick,
  appHref,
  onClick,
  active,
}: {
  title: string
  summary: string
  conversationStatus: TConversationStatus | undefined
  lastSentMessageAt?: string
  /**
   * This prop might not be here in the future, depending on how we will use the actions
   */
  onOptionsClick?: () => void
  /**
   * If provided, the button will navigate to the appHref when clicked
   * the active state will be determined by the useMatch hook
   */
  appHref?: string
  onClick?: () => void
  /**
   * Only used if appHref is not provided
   */
  active?: boolean
}) {
  const { t } = useTranslate()

  const { navigate } = useRouter()

  const isMatch = useMatch(appHref ?? "")

  const isActive = !!appHref ? !!isMatch : !!active

  const isUnresolved = conversationStatus === "unresolved"

  return (
    <Button
      onClick={() => {
        if (appHref) {
          navigate(appHref)
        }

        onClick?.()
      }}
      active={isActive}
    >
      <Text variant="subtitleS" textTransform="capitalize">
        {title}
      </Text>

      <BottomRow>
        <Ellipsis
          variant={isUnresolved ? "subtitleS" : "bodyS"}
          color={isUnresolved ? "primary" : "secondary"}
        >
          {summary}
        </Ellipsis>

        <Text
          variant={isUnresolved ? "subtitleS" : "bodyS"}
          color={isUnresolved ? "primary" : "secondary"}
        >
          {lastSentMessageAt &&
            formatTimeAgo({
              date: lastSentMessageAt,
              locale: "en",
              t: t,
            })}
        </Text>
      </BottomRow>

      {onOptionsClick && (
        <FloatingActions>
          <StyledIconButton
            Icon={OptionsHorizontal}
            variant="subtle"
            color="neutral"
            size="small"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onOptionsClick()
            }}
          />
        </FloatingActions>
      )}
    </Button>
  )
}

const Button = styled.button<{ active?: boolean }>`
  ${ButtonStyleReset}

  display: block;
  text-decoration: none;
  text-align: left;
  background-color: transparent;
  width: 100%;

  position: relative;
  padding-block: ${spacing.S};

  &:before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: calc(${spacing.S} * -1);
    right: calc(${spacing.S} * -1);
    border-radius: 0.75rem;
    background-color: ${({ active }) =>
      active ? colorScale.blueContrast[100] : "transparent"};
    transition: background-color 0.1s;
    z-index: -1;
  }

  &:hover:before {
    background-color: ${colorScale.blueContrast[100]};
  }
`

const BottomRow = styled.div`
  display: grid;
  grid-template-columns: 1fr max-content;
  align-items: center;
  gap: ${spacing.XS};
`

const Ellipsis = styled(Text)`
  display: block;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`

const FloatingActions = styled.div`
  position: absolute;
  top: 50%;
  right: ${spacing.S};
  transform: translateY(-50%);
  opacity: 0;
  transition: opacity 0.1s;

  ${Button}:hover & {
    opacity: 1;
  }
`

const StyledIconButton = styled(IconButton)`
  background-color: ${colors.neutralLight};

  &:hover {
    background-color: ${colors.neutralLight};
  }
`
